import { fromTable } from '@/integrations/supabase/db';
import { SearchResult, SearchSectionId } from '../types';
import { getMatchFunction, getServerPattern, getServerLimit, calculateRelevanceScore } from '../utils';

export async function searchVermittlernummern(
  term: string, 
  limit: number, 
  exactMatch: boolean,
  fuzzyMatch: boolean = false,
  signal?: AbortSignal
): Promise<SearchResult[]> {
  if (!term.trim()) return [];
  
  const pattern = getServerPattern(term, fuzzyMatch);
  const serverLimit = getServerLimit(limit, fuzzyMatch);
  const matchFn = getMatchFunction(exactMatch, fuzzyMatch);
  
  // Step 1: Find matching Gesellschaften
  const { data: matchingGesellschaften } = await fromTable('gesellschaften')
    .select('id, name')
    .ilike('name', pattern)
    .limit(fuzzyMatch ? 100 : 50);
  
  const matchingGesellschaftIds = (matchingGesellschaften || []).map(g => g.id);
  const gesellschaftById = new Map((matchingGesellschaften || []).map(g => [g.id, g.name]));
  
  // Step 2: Find matching Provisionsschemata
  const { data: matchingSchemas } = await fromTable('provisionsschemas')
    .select('id, name, beschreibung')
    .or(`name.ilike.${pattern},beschreibung.ilike.${pattern}`)
    .limit(fuzzyMatch ? 100 : 50);
  
  const matchingSchemaIds = (matchingSchemas || []).map(s => s.id);
  
  // Step 3: Find Vermittlernummer IDs linked to matching schemas
  let vnIdsFromSchemas: string[] = [];
  if (matchingSchemaIds.length > 0) {
    const { data: vnSchemaLinks } = await fromTable('vermittlernummer_provisionsschemas')
      .select('vermittlernummer_id')
      .in('provisionsschema_id', matchingSchemaIds);
    vnIdsFromSchemas = (vnSchemaLinks || []).map(l => l.vermittlernummer_id);
  }
  
  // Step 4: Query Vermittlernummern (by direct match, Gesellschaft, or Schema)
  const orConditions = [
    `vermittlernummer.ilike.${pattern}`,
    `hauptvermittlernummer.ilike.${pattern}`,
    `notiz.ilike.${pattern}`
  ];
  
  if (matchingGesellschaftIds.length > 0) {
    orConditions.push(`gesellschaft_id.in.(${matchingGesellschaftIds.join(',')})`);
  }
  
  if (vnIdsFromSchemas.length > 0) {
    orConditions.push(`id.in.(${vnIdsFromSchemas.join(',')})`);
  }
  
  const { data, error } = await fromTable('gesellschaft_vermittlernummern')
    .select('id, vermittlernummer, hauptvermittlernummer, notiz, gesellschaft_id, updated_at')
    .or(orConditions.join(','))
    .limit(serverLimit);

  if (error) {
    console.error('Vermittlernummern search error:', error);
    return [];
  }

  // Step 5: Load missing Gesellschaften and Schemas
  const allGesellschaftIds = [...new Set((data || []).map(v => v.gesellschaft_id).filter(Boolean))];
  const missingGesellschaftIds = allGesellschaftIds.filter(id => !gesellschaftById.has(id));
  
  if (missingGesellschaftIds.length > 0) {
    const { data: additionalGes } = await fromTable('gesellschaften')
      .select('id, name')
      .in('id', missingGesellschaftIds);
    (additionalGes || []).forEach(g => gesellschaftById.set(g.id, g.name));
  }
  
  // Load schemas for all found Vermittlernummern
  const vnIds = (data || []).map(v => v.id).filter(Boolean);
  const schemasByVnId = new Map<string, string[]>();
  
  if (vnIds.length > 0) {
    const { data: schemaLinks } = await fromTable('vermittlernummer_provisionsschemas')
      .select('vermittlernummer_id, provisionsschema_id')
      .in('vermittlernummer_id', vnIds);
    
    const allSchemaIds = [...new Set((schemaLinks || []).map(l => l.provisionsschema_id))];
    if (allSchemaIds.length > 0) {
      const { data: allSchemas } = await fromTable('provisionsschemas')
        .select('id, name, beschreibung')
        .in('id', allSchemaIds);
      
      const schemaById = new Map((allSchemas || []).map(s => [s.id, s]));
      
      (schemaLinks || []).forEach(link => {
        const schema = schemaById.get(link.provisionsschema_id);
        if (schema) {
          const existing = schemasByVnId.get(link.vermittlernummer_id) || [];
          existing.push(schema.name, schema.beschreibung);
          schemasByVnId.set(link.vermittlernummer_id, existing);
        }
      });
    }
  }

  // Step 6: Multi-term filter with all fields
  return (data || [])
    .filter(v => {
      const gesellschaftName = gesellschaftById.get(v.gesellschaft_id) || '';
      const schemaNames = schemasByVnId.get(v.id)?.join(' ') || '';
      return matchFn(term, 
        v.vermittlernummer, v.hauptvermittlernummer, v.notiz,
        gesellschaftName, schemaNames
      );
    })
    .slice(0, limit)
    .map(v => {
      const title = v.vermittlernummer || v.hauptvermittlernummer || 'Vermittlernummer';
      const subtitle = gesellschaftById.get(v.gesellschaft_id) || v.notiz || undefined;
      
      return {
        id: v.id,
        type: 'vermittlernummern' as SearchSectionId,
        title,
        subtitle,
        badge: schemasByVnId.get(v.id)?.[0] || 'Vermittlernr.',
        data: v,
        relevanceScore: calculateRelevanceScore(term, title, subtitle, v.updated_at)
      };
    });
}
